import React, { useEffect, useState } from "react";
import { C } from "../constants.js";

export function ExtractionReviewPage({ showNotification }) {
  const [queue, setQueue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeReview, setActiveReview] = useState(null);
  const [editValues, setEditValues] = useState({});
  const [pushing, setPushing] = useState(false);
  const threshold = 85;

  const fetchQueue = async () => {
    setLoading(true);
    try {
      const resp = await fetch("/api/extraction-review");
      if (resp.ok) setQueue((await resp.json()).data || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => { fetchQueue(); }, []);

  const startReview = (item) => {
    setActiveReview(item);
    const vals = {};
    (item.fields || []).forEach(f => { vals[f.name] = f.value; });
    setEditValues(vals);
  };

  const approveAndPush = async () => {
    if (!activeReview) return;
    setPushing(true);
    try {
      const resp = await fetch("/api/extraction-review/" + activeReview.id + "/approve", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ reviewed_fields: editValues }),
      });
      if (resp.ok) {
        showNotification && showNotification("Extraction approved and pushed to Clio", "success");
        setActiveReview(null);
        fetchQueue();
      }
    } catch (e) { console.error(e); }
    setPushing(false);
  };

  const rejectReview = async () => {
    if (!activeReview) return;
    try {
      await fetch("/api/extraction-review/" + activeReview.id + "/reject", { method: "POST" });
      showNotification && showNotification("Extraction rejected", "info");
      setActiveReview(null);
      fetchQueue();
    } catch (e) { console.error(e); }
  };

  const confColor = (c) => c >= 95 ? "#10B981" : c >= threshold ? "#F59E0B" : "#EF4444";

  // Review modal
  const reviewModal = activeReview ? React.createElement("div", {
    style: { position:"fixed", inset:0, background:"rgba(0,0,0,0.5)", zIndex:5000, display:"flex", alignItems:"center", justifyContent:"center" },
    onClick: () => setActiveReview(null),
  },
    React.createElement("div", {
      style: { background:"#fff", borderRadius:"16px", padding:"32px", maxWidth:"800px", width:"95%", maxHeight:"85vh", overflowY:"auto" },
      onClick: e => e.stopPropagation(),
    },
      React.createElement("div", { style: { display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"20px" } },
        React.createElement("div", null,
          React.createElement("h3", { style: { margin:0, color:C.navy, fontSize:"18px" } }, "Review Extraction"),
          React.createElement("span", { style: { fontSize:"13px", color:C.textMuted } },
            activeReview.document_type + " \u2014 " + (activeReview.matter_name || "Matter " + activeReview.matter_id)
          ),
        ),
        React.createElement("button", {
          onClick: () => setActiveReview(null),
          style: { background:"none", border:"none", fontSize:"20px", cursor:"pointer", color:C.textMuted },
        }, "\u2715"),
      ),
      // Field review table
      React.createElement("div", { style: { marginBottom:"24px" } },
        (activeReview.fields || []).map(f =>
          React.createElement("div", {
            key: f.name,
            style: { display:"flex", alignItems:"center", gap:"12px", padding:"10px 0", borderBottom:`1px solid ${C.warmGray}40` },
          },
            React.createElement("div", { style: { width:"30%", fontSize:"13px", color:C.textMuted, fontWeight:500 } }, f.name.replace(/_/g, " ")),
            React.createElement("div", {
              style: { width:"8px", height:"8px", borderRadius:"50%", background: confColor(f.confidence), flexShrink:0 },
              title: `Confidence: ${f.confidence}%`,
            }),
            React.createElement("input", {
              value: editValues[f.name] || "",
              onChange: e => setEditValues(v => ({ ...v, [f.name]: e.target.value })),
              style: {
                flex:1, padding:"8px 12px", borderRadius:"8px",
                border: f.confidence < threshold ? "2px solid #EF4444" : `1px solid ${C.warmGray}`,
                fontSize:"14px", color:C.navy, background: f.confidence < threshold ? "#FEF2F2" : "#fff",
              },
            }),
            React.createElement("span", { style: { fontSize:"11px", color: confColor(f.confidence), fontWeight:600, minWidth:"40px", textAlign:"right" } }, f.confidence + "%"),
          ),
        ),
      ),
      // Actions
      React.createElement("div", { style: { display:"flex", gap:"12px", justifyContent:"flex-end" } },
        React.createElement("button", {
          onClick: rejectReview,
          style: { padding:"10px 24px", borderRadius:"8px", border:"1px solid #EF4444", background:"transparent", color:"#EF4444", cursor:"pointer", fontWeight:600, fontSize:"13px" },
        }, "Reject"),
        React.createElement("button", {
          onClick: approveAndPush, disabled: pushing,
          style: { padding:"10px 24px", borderRadius:"8px", border:"none", background:C.gold, color:"#fff", cursor:"pointer", fontWeight:600, fontSize:"13px" },
        }, pushing ? "Pushing..." : "Approve & Push to Clio"),
      ),
    ),
  ) : null;

  return React.createElement("div", { style: { padding:"32px 40px" } },
    reviewModal,
    React.createElement("div", { style: { marginBottom:"24px" } },
      React.createElement("h1", { style: { margin:0, color:C.navy, fontSize:"24px", fontWeight:700 } }, "Extraction Review Queue"),
      React.createElement("p", { style: { margin:"4px 0 0", color:C.textMuted, fontSize:"14px" } },
        `${queue.length} extraction${queue.length !== 1 ? "s" : ""} awaiting review (below ${threshold}% confidence)`
      ),
    ),

    loading ? React.createElement("div", { style: { textAlign:"center", padding:"40px", color:C.textMuted } }, "Loading...") :
    queue.length === 0 ? React.createElement("div", {
      style: { textAlign:"center", padding:"60px", color:C.textMuted, background:"#fff", borderRadius:"12px" },
    },
      React.createElement("div", { style: { fontSize:"48px", marginBottom:"12px" } }, "\u2705"),
      React.createElement("div", { style: { fontSize:"16px", fontWeight:500 } }, "All clear! No extractions need review."),
    ) :
    React.createElement("div", { style: { display:"flex", flexDirection:"column", gap:"12px" } },
      queue.map(item =>
        React.createElement("div", {
          key: item.id,
          style: { background:"#fff", borderRadius:"12px", padding:"20px", boxShadow:"0 1px 3px rgba(0,0,0,0.08)", display:"flex", justifyContent:"space-between", alignItems:"center" },
        },
          React.createElement("div", null,
            React.createElement("div", { style: { fontSize:"15px", fontWeight:600, color:C.navy, marginBottom:"4px" } }, item.document_type.replace(/_/g, " ")),
            React.createElement("div", { style: { fontSize:"13px", color:C.textMuted } }, (item.matter_name || "Matter " + item.matter_id) + " \u2014 " + new Date(item.created_at).toLocaleDateString()),
            React.createElement("div", { style: { display:"flex", gap:"8px", marginTop:"6px", flexWrap:"wrap" } },
              (item.fields || []).filter(f => f.confidence < threshold).map(f =>
                React.createElement("span", {
                  key: f.name,
                  style: { display:"inline-block", padding:"2px 8px", borderRadius:"8px", fontSize:"11px", background:"#FEF2F2", color:"#EF4444", fontWeight:500 },
                }, f.name.replace(/_/g, " ") + " " + f.confidence + "%"),
              ),
            ),
          ),
          React.createElement("button", {
            onClick: () => startReview(item),
            style: { padding:"10px 20px", borderRadius:"8px", border:"none", background:C.gold, color:"#fff", cursor:"pointer", fontWeight:600, fontSize:"13px", whiteSpace:"nowrap" },
          }, "Review"),
        ),
      ),
    ),
  );
}



